/**
 * NER-4488 fase 3 — Plantillas propias a nivel organización (frontend).
 *
 * El backend guarda `{ kind, name, description, payload }` sin interpretar el
 * payload. Acá viven los builders (de un combo / grupo / regla ya armados hacia
 * el payload) y los appliers (del payload hacia el estado de cada pantalla, en
 * OTRO restaurante de la misma organización).
 *
 * Como los ids de categorías, items y grupos son por restaurante, el payload
 * guarda NOMBRES (de categorías e items) y al aplicar se re-matchean por nombre
 * normalizado en el restaurante actual: "bajada directa" a todos los locales.
 */

import { COMBO_TEMPLATE_VERSION, CUSTOM_TEMPLATE_ID } from '../data/comboTemplates';
import { EMPTY_FORM } from './promoMapper';
import { normalizeText, nameMatchesHints } from './text';

export const ORG_TEMPLATE_KINDS = {
  COMBO: 'combo',
  MODIFIER_GROUP: 'modifierGroup',
  PROMO_RULE: 'promoRule',
};

/** Etiqueta visible de un espacio del wizard: texto propio o key i18n. */
export function slotLabel(slot, t) {
  if (slot.label) return slot.label;
  return slot.labelKey ? t(slot.labelKey) : '';
}

// ─── Combos ────────────────────────────────────────────────────────────────

/**
 * Payload de plantilla a partir del estado del wizard de combos.
 * Guarda por espacio: etiqueta, tipo, cantidad/min/max, nombres de categoría
 * (pistas) y nombres de items elegidos con su precio extra.
 */
export function buildComboTemplatePayload({
  slots,
  byId,
  templateId,
  discountPct,
  presentation,
  t,
}) {
  return {
    templateVersion: COMBO_TEMPLATE_VERSION,
    sourceTemplateId: templateId || CUSTOM_TEMPLATE_ID,
    defaultDiscountPct: Math.max(0, Number(discountPct) || 0),
    suggestedName: (presentation?.name || '').trim(),
    description: (presentation?.description || '').trim(),
    badge: (presentation?.badge || '').trim(),
    slots: (slots || []).map((slot) => {
      const items = (slot.itemIds || []).map((id) => byId.get(id)).filter(Boolean);
      const categoryNames = [...new Set(items.map((item) => item.categoryName).filter(Boolean))];
      const extraPricesByName = {};
      for (const item of items) {
        const extra = Number(slot.extraPrices?.[item.itemId] || 0);
        if (extra > 0) extraPricesByName[item.name] = extra;
      }
      const base = {
        slotId: slot.slotId,
        kind: slot.kind,
        label: slotLabel(slot, t),
        categoryHints: categoryNames,
        itemNames: items.map((item) => item.name),
      };
      if (slot.kind === 'fixed') base.quantity = Number(slot.quantity) || 1;
      else {
        base.min = Number(slot.min) || 0;
        base.max = Math.max(1, Number(slot.max) || 1);
        base.extraPricesByName = extraPricesByName;
      }
      return base;
    }),
  };
}

/** Primer `categoryId` cuyo nombre matchea alguna pista (o ''). */
function matchCategoryId(categories, hints) {
  if (!hints || hints.length === 0) return '';
  // Match exacto primero, después parcial.
  const wanted = hints.map((hint) => normalizeText(hint).trim()).filter(Boolean);
  const exact = (categories || []).find((cat) => wanted.includes(normalizeText(cat.name).trim()));
  if (exact) return exact.categoryId;
  const partial = (categories || []).find((cat) => nameMatchesHints(cat.name, hints));
  return partial ? partial.categoryId : '';
}

/**
 * Items del restaurante actual cuyo nombre coincide con `names` (normalizado).
 * Prefiere los de `preferredCategoryId`; ignora combos.
 */
function matchItemsByName(categories, names, preferredCategoryId) {
  const wanted = new Map((names || []).map((name) => [normalizeText(name).trim(), name]));
  if (wanted.size === 0) return [];
  const found = new Map();
  const consider = (cat) => {
    for (const item of cat.items || []) {
      if (item.isCombo || item.status === 'deleted') continue;
      const key = normalizeText(item.name).trim();
      if (wanted.has(key) && !found.has(key))
        found.set(key, { ...item, categoryId: cat.categoryId });
    }
  };
  const preferred = (categories || []).find((cat) => cat.categoryId === preferredCategoryId);
  if (preferred) consider(preferred);
  for (const cat of categories || []) consider(cat);
  return [...found.values()];
}

/**
 * Estado de espacios del wizard a partir del payload de una plantilla propia,
 * re-matcheando categorías e items por nombre en el restaurante actual.
 */
export function slotsFromComboTemplatePayload(payload, categories) {
  return (payload?.slots || []).map((saved, index) => {
    const categoryId = matchCategoryId(categories, saved.categoryHints);
    const matched = matchItemsByName(categories, saved.itemNames, categoryId);
    const extraPrices = {};
    for (const item of matched) {
      const extra = saved.extraPricesByName?.[item.name];
      if (extra) extraPrices[item.itemId] = Number(extra) || 0;
    }
    const kind = saved.kind === 'fixed' ? 'fixed' : 'choice';
    return {
      slotId: saved.slotId || `${kind}-${index}`,
      kind,
      label: saved.label || '',
      labelKey: saved.label
        ? undefined
        : `comboWizard.slots.custom${kind === 'fixed' ? 'Fixed' : 'Choice'}`,
      quantity: kind === 'fixed' ? Number(saved.quantity) || 1 : undefined,
      min: kind === 'choice' ? Number(saved.min) || 0 : undefined,
      max: kind === 'choice' ? Math.max(1, Number(saved.max) || 1) : undefined,
      categoryId: categoryId || matched[0]?.categoryId || '',
      itemIds:
        kind === 'fixed'
          ? matched.slice(0, 1).map((item) => item.itemId)
          : matched.map((item) => item.itemId),
      extraPrices,
      groupId: null,
    };
  });
}

// ─── Grupos de modificadores ───────────────────────────────────────────────

/** Payload de plantilla a partir de un grupo de modificadores existente. */
export function buildModifierGroupTemplatePayload(group) {
  return {
    name: group.name,
    minSelect: Number(group.minSelect) || 0,
    maxSelect: Math.max(1, Number(group.maxSelect) || 1),
    required: Boolean(group.required),
    options: (group.options || []).map((opt) => ({
      name: opt.name,
      price: Number(opt.price) || 0,
    })),
  };
}

/** Estado del formulario de ModifiersPage a partir del payload. */
export function groupFromTemplatePayload(payload) {
  return {
    name: payload?.name || '',
    minSelect: Number(payload?.minSelect) || 0,
    maxSelect: Math.max(1, Number(payload?.maxSelect) || 1),
    required: Boolean(payload?.required),
    options: (payload?.options || []).map((opt) => ({
      name: opt.name || '',
      price: Number(opt.price) || 0,
    })),
  };
}

// ─── Reglas de promoción ───────────────────────────────────────────────────

/**
 * Payload de plantilla a partir de una regla (shape anidado del backend).
 * Los targets se guardan como nombres de categorías / items.
 */
export function buildPromoTemplatePayload(rule, menuData) {
  const categoryNames = [];
  const itemNames = [];
  const categoryIds = new Set(rule?.targets?.categoryIds || []);
  const itemIds = new Set(rule?.targets?.itemIds || []);
  for (const cat of menuData || []) {
    if (categoryIds.has(cat.categoryId)) categoryNames.push(cat.name);
    for (const item of cat.items || []) {
      if (itemIds.has(item.itemId)) itemNames.push(item.name);
    }
  }
  return {
    type: rule?.type || 'happy_hour',
    days: [...(rule?.schedule?.days || [])],
    start: rule?.schedule?.start || '00:00',
    end: rule?.schedule?.end || '23:59',
    discountType: rule?.discount?.type || 'percentage',
    discountValue: Number(rule?.discount?.value) || 0,
    categoryNames,
    itemNames,
  };
}

/** Form plano de PromotionsPage a partir del payload, re-matcheando por nombre. */
export function promoFormFromTemplatePayload(payload, menuData, name) {
  const wantedCats = new Set((payload?.categoryNames || []).map((n) => normalizeText(n).trim()));
  const wantedItems = new Set((payload?.itemNames || []).map((n) => normalizeText(n).trim()));
  const categoryIds = [];
  const itemIds = [];
  for (const cat of menuData || []) {
    if (cat.status === 'deleted') continue;
    if (wantedCats.has(normalizeText(cat.name).trim())) categoryIds.push(cat.categoryId);
    for (const item of cat.items || []) {
      if (wantedItems.has(normalizeText(item.name).trim())) itemIds.push(item.itemId);
    }
  }
  return {
    ...EMPTY_FORM,
    name: name || '',
    type: payload?.type || 'happy_hour',
    days: [...(payload?.days || [])],
    startTime: payload?.start || '00:00',
    endTime: payload?.end || '23:59',
    discountType: payload?.discountType || 'percentage',
    discountValue: Number(payload?.discountValue) || 0,
    categoryIds,
    itemIds,
    isActive: true,
  };
}

// ─── Presentación ──────────────────────────────────────────────────────────

/** Chips descriptivos de una plantilla propia para el TemplatePicker. */
export function describeOrgTemplate(template, t) {
  const payload = template?.payload || {};
  if (template.kind === ORG_TEMPLATE_KINDS.COMBO) {
    const slots = payload.slots || [];
    const fixed = slots.filter((s) => s.kind === 'fixed').length;
    const choice = slots.length - fixed;
    const chips = [];
    if (fixed) chips.push(t('templates.own.chips.fixedSlots', { count: fixed }));
    if (choice) chips.push(t('templates.own.chips.choiceSlots', { count: choice }));
    if (payload.defaultDiscountPct > 0) chips.push(`−${payload.defaultDiscountPct}%`);
    return chips;
  }
  if (template.kind === ORG_TEMPLATE_KINDS.MODIFIER_GROUP) {
    return [
      t('templates.chips.optionsCount', { count: (payload.options || []).length }),
      payload.required ? t('common.required') : t('common.optional'),
    ];
  }
  if (template.kind === ORG_TEMPLATE_KINDS.PROMO_RULE) {
    const chips = [];
    if (payload.type) chips.push(t(`promotions.types.${payload.type}`));
    if (payload.discountType === 'percentage') chips.push(`${payload.discountValue}%`);
    else if (payload.discountValue) chips.push(`$${payload.discountValue}`);
    chips.push(
      payload.categoryNames?.length || payload.itemNames?.length
        ? t('templates.own.chips.targets', {
            count: (payload.categoryNames?.length || 0) + (payload.itemNames?.length || 0),
          })
        : t('templates.chips.wholeMenu')
    );
    return chips;
  }
  return [];
}
